import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { getInBuilding } from "../../lib/api";

const fmtTime = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleTimeString("en-US", { timeZone: "America/New_York", hour: "numeric", minute: "2-digit" });
  } catch { return ""; }
};

function PersonList({ title, people, accent, testid }) {
  return (
    <Card className={accent} data-testid={testid}>
      <CardContent className="py-3">
        <div className="flex items-center justify-between mb-2">
          <span className="font-semibold">{title}</span>
          <Badge variant="outline" className="text-base font-bold">{people.length}</Badge>
        </div>
        <div className="space-y-1 max-h-72 overflow-y-auto">
          {people.length === 0 ? (
            <p className="text-xs text-muted-foreground">None</p>
          ) : people.map((p) => (
            <div key={p.id} className="text-sm flex items-center justify-between border-b last:border-0 py-0.5">
              <span>{p.name}{p.group ? <span className="text-muted-foreground"> · {p.group}</span> : null}</span>
              <span className="text-xs text-muted-foreground">{p.reason || (p.check_in ? `in ${p.check_in}` : "")}</span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

export function HeadCountTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await getInBuilding();
      setData(res.data);
    } catch {
      toast.error("Failed to load head count");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000); // auto-refresh every minute
    return () => clearInterval(t);
  }, [load]);

  if (loading) return <Card><CardContent className="py-8 text-center text-muted-foreground">Loading head count…</CardContent></Card>;
  if (!data) return null;

  const c = data.counts;

  return (
    <div className="space-y-4" data-testid="headcount-tab">
      <div className="flex flex-wrap items-center gap-4 justify-between">
        <div>
          <div className="text-sm text-muted-foreground">Students currently in the building</div>
          <div className="text-6xl font-extrabold text-emerald-700" data-testid="headcount-in-building-number">{c.in_building}</div>
          <div className="text-xs text-muted-foreground">of {c.total_expected} expected today · updated {fmtTime(data.generated_at)} ET</div>
        </div>
        <Button variant="outline" onClick={load} data-testid="headcount-refresh-btn">Refresh now</Button>
      </div>

      <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800">
        🔔 <strong>Evacuation count:</strong> expect to physically account for <strong>{c.in_building}</strong> student(s).
        {c.off_site > 0 && <> {c.off_site} are off-site (internship/checked out) and should NOT be counted.</>}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <PersonList title="✅ In Building" people={data.in_building} accent="border-emerald-300" testid="headcount-in-building" />
        <PersonList title="🚗 Off-site / Left" people={data.off_site} accent="border-amber-300" testid="headcount-off-site" />
        <PersonList title="❌ Absent / Not in" people={data.absent} accent="border-slate-300" testid="headcount-absent" />
      </div>
      {data.not_expected.length > 0 && (
        <p className="text-xs text-muted-foreground">{data.not_expected.length} student(s) not expected today (not enrolled / not scheduled) and excluded from the count.</p>
      )}
    </div>
  );
}
